import { Button, FormGroup, Input, Label, Select } from '@/components'
import { FlexWrapper } from '@/components/layout/flex/flexWrapper'
import { useAxios } from '@/context/axiosContext'
import { useDialog } from '@/context/dialogContext'
import { useRedis } from '@/hooks/useRedis'
import { DataRow } from '@/pages/member/memberMgmt'
import commonUtils from '@/utils/commonUtils'
import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Col, Row } from 'styled-bootstrap-grid'

type TInput = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement

interface GetRequest {
  cstId?: string
  userId?: string
}

const genderList = [
  { code: '', codeNm: '' },
  { code: 'M', codeNm: '남성' },
  { code: 'F', codeNm: '여성' },
]

const DetailMemberMgmt: React.FC = () => {
  const axios = useAxios()
  const navigate = useNavigate()
  const location = useLocation()
  const { showAlert, showConfirm } = useDialog()
  const [requestData] = useState({
    userId: String(location.state?.userId),
    cstId: String(location.state?.cstId),
  })

  const [memberInfoMap, setMemberInfoMap] = useState<DataRow | null>(null)

  const { getComCodes } = useRedis()

  const classNameMap: Record<string, string> = {
    Y: 'option-blue',
    A: '',
    B: '',
    D: '',
    X: 'option-red',
  }

  useEffect(() => {
    getMemberInfo()
  }, [])

  const handleInputChange = (e: React.ChangeEvent<TInput>) => {
    const { name, value } = e.target

    setMemberInfoMap((prevState) => {
      return {
        ...prevState,
        [name]: value,
      } as DataRow // 모든 필드를 포함하는 상태를 유지
    })
  }

  //회원정보조회
  const getMemberInfo = async () => {
    try {
      const data: GetRequest = {
        userId: requestData.userId,
        cstId: requestData.cstId,
      }

      const response = await axios.get('/member/info', {
        params: { ...data },
      })

      const memberInfoMap = response.data
      setMemberInfoMap(memberInfoMap)
    } catch (err: any) {
      console.error('Error fetching detailBiz:', err)
    }
  }

  // TODO. 상태, 비밀번호 초기화 외 관리자가 직접 변경 가능하게 할 지 확인
  // 회원정보 수정
  const updateInfo = async () => {
    try {
      const response = await axios.post('/member/info', memberInfoMap, {
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (response.status === 200) {
        showAlert({
          type: 'success',
          title: '저장이 완료되었어요.',
          onClose() {
            navigate('/bizMgmt')
          },
        })
      } else {
        showAlert({
          title: '저장을 실패했어요. 잠시 후 다시 시도해 주세요.',
        })
      }
    } catch (err: any) {
      showAlert({
        title: '오류가 발생했어요. 잠시 후 다시 시도해주세요.',
      })
      console.error('Error updating biz:', err)
    }
  }

  //변경버튼 클릭시 회원상태변경
  const handleStateUpdate = async () => {
    try {
      const response = await axios.post(
        '/member/updtState',
        {
          state: memberInfoMap?.state,
          userId: requestData.userId,
          cstId: requestData.cstId,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )

      if (response.status === 200) {
        showAlert({
          type: 'success',
          title: '회원 상태가 변경됐어요.',
        })
      } else {
        showAlert({
          title: '저장을 실패했어요. 잠시 후 다시 시도해주세요.',
        })
      }
    } catch (err: any) {
      showAlert({
        title: '오류가 발생했어요. 잠시 후 다시 시도해주세요.',
      })
      console.error('Error reset member:', err)
    }
  }

  //초기화버튼 클릭시 비밀번호 초기화
  const handlePwdReset = async () => {
    const reqMap = {
      cstId: requestData.cstId,
      userId: requestData.userId,
    }

    try {
      const response = await axios.post('/member/reset', reqMap, {
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (response.status === 200) {
        showAlert({
          type: 'success',
          title: '비밀번호가 USER ID로 초기화 됐어요.',
        })
      } else {
        showAlert({
          title: '비밀번호가 초기화를 실패했어요.',
        })
      }
    } catch (err: any) {
      console.error('Error reset member:', err)
      showAlert({
        title: '비밀번호가 초기화를 실패했어요.',
      })
    }
  }

  return (
    <>
      <div style={{ width: '100%', height: '100%', marginTop: '50px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            marginBottom: '10px',
            alignItems: 'center',
            marginTop: '50px',
          }}
        >
          <p style={{ fontSize: '20px' }}>회원상세 관리</p>
          <FlexWrapper gap="20px">
            <Button
              onClick={() =>
                showConfirm({
                  title: '목록으로 이동할까요?',
                  onConfirm() {
                    navigate('/memberMgmt', {
                      state: {
                        prevSearchState: location.state?.prevSearchState,
                      },
                    })
                  },
                })
              }
              variant="outlined"
              color="neverbe"
            >
              목록
            </Button>
            <Button
              onClick={() =>
                showConfirm({
                  title: '비밀번호를 초기화할까요?',
                  onConfirm() {
                    handlePwdReset()
                  },
                })
              }
              color="neverbe"
            >
              비밀번호 초기화
            </Button>
          </FlexWrapper>
        </div>
        <Row style={{ marginTop: '20px' }}>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="cstId">
                회원번호
              </Label>
              <Input
                id="cstId"
                name="cstId"
                placeholder="회원번호"
                maxLength={20}
                value={memberInfoMap?.cstId || ''}
                disabled={true}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="userId">
                ID
              </Label>
              <Input
                id="userId"
                name="userId"
                placeholder="ID"
                maxLength={100}
                value={memberInfoMap?.userId || ''}
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="state">
                상태
              </Label>
              <FlexWrapper justifyContent="space-between" gap="5px">
                <Select
                  id="state"
                  name="state"
                  width={'70%'}
                  value={memberInfoMap?.state || ''}
                  onChange={handleInputChange}
                  className={
                    memberInfoMap?.state === 'X'
                      ? 'select-red'
                      : memberInfoMap?.state === 'Y'
                        ? 'select-blue'
                        : 'select-default'
                  }
                >
                  {getComCodes('MEM_STATE').map((item) => (
                    <option
                      key={item.code}
                      value={item.code}
                      className={classNameMap[item.code] || ''}
                    >
                      {item.codeNm}
                    </option>
                  ))}
                </Select>
                <Button
                  size="xs"
                  height="38px"
                  variant="outlined"
                  color="neverbe"
                  onClick={handleStateUpdate}
                >
                  상태변경
                </Button>
              </FlexWrapper>
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="name">
                이름
              </Label>
              <Input
                id="name"
                name="name"
                placeholder="이름"
                maxLength={100}
                value={memberInfoMap?.name || ''}
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="nickName">
                닉네임
              </Label>
              <Input
                id="nickName"
                name="nickName"
                placeholder="닉네임"
                maxLength={100}
                value={memberInfoMap?.nickName || ''}
                onChange={handleInputChange}
                disabled={true}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="gender">
                성별
              </Label>

              <Select
                id="gender"
                name="gender"
                value={memberInfoMap?.gender || ''}
                onChange={handleInputChange}
                disabled={true}
              >
                {genderList.map(({ code, codeNm }) => (
                  <option key={code} value={code}>
                    {codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="accessIp">
                접속IP
              </Label>
              <Input
                id="accessIp"
                name="accessIp"
                placeholder="접속IP"
                maxLength={100}
                value={memberInfoMap?.accessIp || ''}
                onChange={handleInputChange}
                disabled={true}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="birth">
                생년월일
              </Label>
              <Input
                id="birth"
                name="birth"
                placeholder="생년월일"
                maxLength={100}
                value={memberInfoMap?.birth || ''}
                onChange={handleInputChange}
                disabled={true}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="email">
                이메일
              </Label>
              <Input
                id="email"
                name="email"
                placeholder="이메일"
                maxLength={100}
                value={memberInfoMap?.email || ''}
                onChange={handleInputChange}
                disabled={true}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="smsOptIn">
                SMS수신여부
              </Label>
              <Input
                id="smsOptIn"
                name="smsOptIn"
                placeholder="SMS수신여부"
                maxLength={200}
                value={memberInfoMap?.smsOptIn || ''}
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="jobCd">
                직업
              </Label>
              <Input
                id="jobCd"
                name="jobCd"
                placeholder="직업"
                maxLength={50}
                value={memberInfoMap?.jobCd || ''}
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="pwErrCount">
                비밀번호 오류횟수
              </Label>
              <Input
                id="pwErrCount"
                name="pwErrCount"
                placeholder="비밀번호 오류횟수"
                maxLength={2}
                value={memberInfoMap?.pwErrCount || ''}
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="mnc">
                통신사
              </Label>
              <Input
                id="mnc"
                name="mnc"
                placeholder="통신사"
                maxLength={20}
                value={memberInfoMap?.mnc || ''}
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="mblNetCd">
                휴대폰식별번호
              </Label>
              <Input
                id="mblNetCd"
                name="mblNetCd"
                placeholder="휴대폰식별번호"
                maxLength={20}
                value={memberInfoMap?.mblNetCd || ''}
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="mblExcNo">
                휴대전화국번
              </Label>
              <Input
                id="mblExcNo"
                name="mblExcNo"
                placeholder="휴대전화국번"
                maxLength={20}
                value={memberInfoMap?.mblExcNo || ''}
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="mblSubNo">
                휴대전화개인번호
              </Label>
              <Input
                id="mblSubNo"
                name="mblSubNo"
                maxLength={20}
                value={memberInfoMap?.mblSubNo || ''}
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="homePostCd">
                자택우편번호
              </Label>
              <Input
                id="homePostCd"
                name="homePostCd"
                placeholder="자택우편번호"
                maxLength={200}
                value={memberInfoMap?.homePostCd || ''}
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="homeAddr">
                자택주소
              </Label>
              <Input
                id="homeAddr"
                name="homeAddr"
                placeholder="자택주소"
                maxLength={200}
                value={memberInfoMap?.homeAddr || ''}
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="homeDaddr">
                자택상세주소
              </Label>
              <Input
                id="homeDaddr"
                name="homeDaddr"
                placeholder="자택상세주소"
                maxLength={200}
                value={memberInfoMap?.homeDaddr || ''}
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="homeArCd">
                자택지역번호
              </Label>
              <Input
                id="homeArCd"
                name="homeArCd"
                placeholder="자택지역번호"
                maxLength={100}
                value={memberInfoMap?.homeArCd || ''}
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="homeExcNo">
                자택국번
              </Label>
              <Input
                id="homeExcNo"
                name="homeExcNo"
                placeholder="자택국번"
                maxLength={20}
                value={memberInfoMap?.homeExcNo || ''}
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="homeSubNo">
                자택개인번호
              </Label>
              <Input
                id="homeSubNo"
                name="homeSubNo"
                maxLength={20}
                value={memberInfoMap?.homeSubNo || ''}
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="wrcPostCd">
                직장우편번호
              </Label>
              <Input
                id="wrcPostCd"
                name="wrcPostCd"
                maxLength={20}
                value={memberInfoMap?.wrcPostCd || ''}
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="wrcAddr">
                직장주소
              </Label>
              <Input
                id="wrcAddr"
                name="wrcAddr"
                maxLength={20}
                value={memberInfoMap?.wrcAddr || ''}
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="wrcDaddr">
                직장상세주소
              </Label>
              <Input
                id="wrcDaddr"
                name="wrcDaddr"
                maxLength={20}
                value={memberInfoMap?.wrcDaddr || ''}
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="wrcArCd">
                직장지역번호
              </Label>
              <Input
                id="wrcArCd"
                name="wrcArCd"
                maxLength={20}
                value={memberInfoMap?.wrcArCd || ''}
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="wrcExcNo">
                직장국번
              </Label>
              <Input
                id="wrcExcNo"
                name="wrcExcNo"
                maxLength={20}
                value={memberInfoMap?.wrcExcNo || ''}
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="wrcSubNo">
                직장개인번호
              </Label>
              <Input
                id="wrcSubNo"
                name="wrcSubNo"
                maxLength={20}
                value={memberInfoMap?.wrcSubNo || ''}
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="wrcNm">
                직장명
              </Label>
              <Input
                id="wrcNm"
                name="wrcNm"
                maxLength={20}
                value={memberInfoMap?.wrcNm || ''}
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="department">
                부서
              </Label>
              <Input
                id="department"
                name="department"
                maxLength={20}
                value={memberInfoMap?.department || ''}
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="jbps">
                직위
              </Label>
              <Input
                id="jbps"
                name="jbps"
                maxLength={20}
                value={memberInfoMap?.jbps || ''}
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="lastLogin">
                최종로그인일시
              </Label>
              <Input
                id="lastLogin"
                name="lastLogin"
                maxLength={20}
                value={memberInfoMap?.lastLogin || ''}
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="accessIp">
                접속IP
              </Label>
              <Input
                id="accessIp"
                name="accessIp"
                maxLength={20}
                value={memberInfoMap?.accessIp || ''}
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="withDrawDttm">
                탈퇴일시
              </Label>
              <Input
                id="withDrawDttm"
                name="withDrawDttm"
                maxLength={20}
                value={memberInfoMap?.withDrawDttm || ''}
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="regId">
                등록자
              </Label>
              <Input
                id="regId"
                name="regId"
                maxLength={20}
                value={memberInfoMap?.regId || ''}
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="regDate">
                등록일시
              </Label>
              <Input
                id="regDate"
                name="regDate"
                maxLength={20}
                value={
                  (memberInfoMap?.regDate &&
                    commonUtils.formatDate(memberInfoMap?.regDate, true)) ||
                  ''
                }
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="updId">
                수정자
              </Label>
              <Input
                id="updId"
                name="updId"
                maxLength={20}
                value={memberInfoMap?.updId || ''}
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="updDate">
                수정일시
              </Label>
              <Input
                id="updDate"
                name="updDate"
                maxLength={20}
                value={
                  (memberInfoMap?.updDate &&
                    commonUtils.formatDate(memberInfoMap?.updDate, true)) ||
                  ''
                }
                disabled={true}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
        </Row>
      </div>
    </>
  )
}

export default DetailMemberMgmt
